import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import {Layout} from "components/UI/layout";
import {Preloader} from "components/Preloader";
import {dateFormatUTC} from "helpers/datetime";
import {Tag} from "components/Tag";
import {IconCalendar, IconFactory, IconChevronLeft, IconAi} from "components/icons";
import {Info} from "components/Info";
import {color} from "constants/color.consts";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {ShareButton} from "components/buttons/ShareButton";
import {PUBLIC_RFTS} from "constants/routes.const";
import {copyToClipboard} from "helpers/helper";

const stockholmDateFormatter = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Stockholm",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const toStockholmDate = (date) => stockholmDateFormatter.format(date);

const languageKeys = {
  sv: "richTextHtml",
  fi: "richTextHtmlFinnish",
  en: "richTextHtmlEnglish",
};

const ExternalRftPage = inject("store")(
  observer(({store: {seller, clientStore}}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {state} = useLocation();
    const {externalRftId} = useParams();
    const {language} = clientStore;
    const [loading, setLoading] = useState(true);
    const [loadingAnnouncement, setLoadingAnnouncement] = useState(true);
    const [externalRft, setExternalRft] = useState(state?.externalRft ?? null);
    const [externalAnnouncement, setExternalAnnouncement] = useState(null);

    const availableIn = externalRft?.availableIn ?? [];
    const categories = [
      ...(externalRft?.consultingServices ?? []),
      ...(externalRft?.entrepreneurOffers ?? []),
      ...(externalRft?.entrepreneurWithin ?? []),
      ...(externalRft?.materialSupplierType ?? []),
    ];

    const canRecheck = useMemo(() => {
      if (loading || !externalAnnouncement?.announcedAt) return false;
      const lastChecked = new Date(
        externalAnnouncement.announcedAt.endsWith("Z")
          ? externalAnnouncement.announcedAt
          : externalAnnouncement.announcedAt + "Z"
      );
      return lastChecked < toStockholmDate(new Date());
    }, [loading, externalAnnouncement?.announcedAt]);

    const announcementRichTextHtml = useMemo(
      () =>
        loadingAnnouncement
          ? ""
          : externalAnnouncement?.[languageKeys[language]] ||
            externalAnnouncement?.richTextHtml ||
            "",
      [loadingAnnouncement, externalAnnouncement, language]
    );

    const fetchExternalRft = useCallback(() => {
      if (externalRft) return;
      setLoading(true);
      seller
        .getExternalRequest(externalRftId)
        .then((data) => setExternalRft(data))
        .finally(() => setLoading(false));
    }, [seller, externalRftId, externalRft]);

    const getExternalRftAnnouncement = useCallback(
      () =>
        seller.getExternalRftAnnouncement(externalRftId).then(setExternalAnnouncement),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const handleOpenSource = (event) => {
      const url = externalRft?.resourceUrl;
      if (!url) return;
      event.preventDefault();

      const isPdf = url.toLowerCase().includes("pdf");
      const sourceTab = window.open(url, "_blank");
      if (!sourceTab) return;
      sourceTab.opener = null;
      if (!isPdf) return;
      setTimeout(() => !sourceTab.closed && sourceTab.close(), 5000);
    };

    const copyLink = () => copyToClipboard(window.location, true);

    useEffect(() => {
      fetchExternalRft();
    }, [fetchExternalRft]);

    useEffect(() => {
      if (!externalAnnouncement?.richTextHtml) {
        getExternalRftAnnouncement();
        const checkAnnouncement = setInterval(() => getExternalRftAnnouncement(), 10000);
        return () => clearInterval(checkAnnouncement);
      } else setLoadingAnnouncement(false);
    }, [getExternalRftAnnouncement, externalAnnouncement?.richTextHtml]);

    return (
      <Layout>
        <div className="content-block_wrapper pa-8">
          <div className="df-row-jsb mb-8">
            <ButtonTransparent
              icon={<IconChevronLeft color={color.blue} />}
              noBorder
              uppercase
              bold
              small
              addClass="back-button pl-0"
              name="PUBLIC_RFPS_PAGE_NAVIGATION_BACK"
              needTranslation
              onClick={() => navigate(PUBLIC_RFTS)}
            />
            <ShareButton name="SHARE_THIS" onClickCopy={copyLink} />
          </div>
          <div className="mb-4 df-row-center">
            <IconAi animation="swing" addClass="mr-2" size={20} />
            <Tag
              text="AI_SUMMARY_LABEL"
              needTranslation
              noBackground
              color={color.black}
              borderColor={color.lightGray}
              medium
              normal
            />
            <Info addClass="df" tooltip="DISCLAIMER_AI_CONTENT" />
            {externalAnnouncement?.updatedAt && (
              <div className="fz-12 ml-4 df-row-center">
                {t("EXTERNAL_RFT_PAGE_ANNOUNCEMENT_UPDATED_AT")}:{" "}
                {dateFormatUTC(externalAnnouncement?.announcedAt)}
                <ButtonTransparent
                  name="EXTERNAL_RFT_PAGE_ANNOUNCEMENT_CHECK_AGAIN"
                  tiny
                  rounded
                  addClass="ml-2"
                  disabled={!canRecheck}
                  onClick={getExternalRftAnnouncement}
                />
                <Info
                  tooltip="EXTERNAL_RFT_PAGE_ANNOUNCEMENT_CHECK_AGAIN_TOOLTIP"
                  addClass="ml-1"
                />
              </div>
            )}
            <div className="df-row-center ml-8">
              {categories.map((category) => (
                <Tag
                  key={category}
                  text={category}
                  needTranslation
                  color={color.blue}
                  medium
                  normal
                  round
                  addClass="fz-12 pr-2 pl-2"
                />
              ))}
              {availableIn.map((region) => (
                <Tag
                  key={region}
                  text={region}
                  needTranslation
                  color={color.green}
                  medium
                  normal
                  round
                  addClass="fz-12 pr-2 pl-2"
                />
              ))}
            </div>
          </div>
          <h2 className="title mb-4">{externalRft?.name ?? "-"}</h2>
          <div className="mb-8 df-row-center fz-14">
            <span className="mr-4 df-row-center">
              <IconFactory addClass="mr-2" />
              {externalRft?.Buyer?.name ?? externalRft?.buyerName ?? "-"}
            </span>
            <span className="mr-4 df-row-center">
              <IconCalendar addClass="mr-2" />
              {t("PUBLIC_RFPS_PAGE_TABLE_COLUMN_08")}:{" "}
              {externalRft?.publishedAt ? dateFormatUTC(externalRft.publishedAt) : "-"}
            </span>
            <span className="mr-4 df-row-center">
              <IconCalendar addClass="mr-2" />
              {t("PUBLIC_RFPS_PAGE_TABLE_COLUMN_03")}:{" "}
              {externalRft?.closedAt ? dateFormatUTC(externalRft.closedAt) : "-"}
            </span>
          </div>
          {externalRft?.resourceUrl && (
            <ButtonTransparent
              name="EXTERNAL_RFT_PAGE_SOURCE_LINK"
              onClick={handleOpenSource}
              small
            />
          )}
          <div className="external-rft-announcement mt-4">
            {loadingAnnouncement ? (
              <div className="df-column-center fit-width">
                <Preloader addClass="preloader100" size="small" colorful />
                <div
                  className="df-row-jcc-center fz-16 __italic mt-[-20]"
                  style={{animation: "color-shimmer 1.8s ease-in-out infinite"}}
                >
                  {t("EXTERNAL_RFT_PAGE_ANNOUNCEMENT_LOADING")}
                </div>
              </div>
            ) : announcementRichTextHtml ? (
              <>
                <Tag
                  text="AI_SUMMARY_LABEL"
                  needTranslation
                  noBackground
                  color={color.black}
                  borderColor={color.lightGray}
                  medium
                  normal
                  addClass="mt-8 mb-4"
                />

                <div
                  className="external-rft-announcement_content"
                  dangerouslySetInnerHTML={{__html: announcementRichTextHtml}}
                />
              </>
            ) : null}
          </div>
        </div>
      </Layout>
    );
  })
);

export default ExternalRftPage;
